import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, Renderer2, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, NgIf } from '@angular/common';
import { Subscription, timeout } from 'rxjs';

import { OrderContentComponent } from '../../shared/components/order-content/order-content.component';
import { OrderService } from '../../core/services/order.service';
import { AuthService } from '../../core/services/auth.service';
import {
  CustomerOrderLineItem,
  OrderData,
  OrderDetailsResponse,
  OrderProduct,
} from '../../core/models/order.model';
import {
  buildRetryCartQueryParams,
  retryLinesFromOrderData,
} from '../../core/utils/order-retry-cart.util';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { GtmService, Ga4Item } from '../../core/services/gtm.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { Icon } from '../../core/models/icon.model';

@Component({
  selector: 'umkt-order-details',
  standalone: true,
  imports: [
    OrderContentComponent,
    BreadcrumbComponent,
    NgIf,
    LoadingComponent,
    I18nPipe,
    IconComponent,
  ],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss',
})
export class OrderDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  orderId: string = '';
  orderDetails: OrderData = {} as OrderData;
  isLoading: boolean = false;
  error: string | null = null;
  breadCrump: BreadcrumbItem[] = [];
  currentUserId: string | null = null;
  showCreditRejectedRetry = false;

  readonly creditRetryBannerIcon: Icon = {
    name: 'circulo-exclamacao',
    type: 'mondrian-icons',
  };

  private langSubscription?: Subscription;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private orderService = inject(OrderService);
  private authService = inject(AuthService);
  private location = inject(Location);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private gtm = inject(GtmService);
  private i18nService = inject(I18nService);


  ngOnInit(): void {
    this.currentUserId = this.authService.getCurrentUserUuid();

    this.orderId = this.route.snapshot.paramMap.get('id') || 
                   this.orderService.getSelectedOrderId() || '';

    if (!this.orderId) {
      this.error = 'ID do pedido não encontrado.';
      this.isLoading = false;
      return;
    }

    const navState = this.location.getState() as {
      creditAnalysisRejected?: boolean;
    };
    const fromQuery =
      this.route.snapshot.queryParamMap.get('creditRejected') === '1';
    const fromState = navState?.creditAnalysisRejected === true;
    const fromService = this.orderService.isCreditAnalysisRejectedForOrder(
      this.orderId
    );
    this.showCreditRejectedRetry = fromQuery || fromState || fromService;

    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.buildBreadcrumb();
    });
    this.loadOrderData();
  }

  get showCreditRetryBanner(): boolean {
    if (!this.showCreditRejectedRetry || this.isLoading || this.error) {
      return false;
    }
    if ((this.orderDetails?.paymentMethod ?? '') === 'MIND') {
      return false;
    }
    return retryLinesFromOrderData(this.orderDetails).length > 0;
  }

  goToCartRetryWithCard(): void {
    if ((this.orderDetails?.paymentMethod ?? '') === 'MIND') {
      return;
    }
    const params = buildRetryCartQueryParams(retryLinesFromOrderData(this.orderDetails));
    if (!params) {
      return;
    }
    this.router.navigate(['/'], { queryParams: params });
  }

  ngAfterViewInit(): void {
    const breadcrumpsContainer =
      this.el.nativeElement.querySelector('.breadcrumps');

    if (breadcrumpsContainer) {
      const targetElements = breadcrumpsContainer.querySelectorAll('a, span');
      const targetElementsI = breadcrumpsContainer.querySelectorAll('i');
      const targetLastElement =
        breadcrumpsContainer.querySelector('.last-of-type');

      targetElementsI.forEach((element: any) => {
        this.renderer.setStyle(element, 'font-size', '22px');
      });

      targetElements.forEach((element: any, index: number) => {
        const isLastElement = index === targetElements.length - 1;

        this.renderer.listen(element, 'mouseenter', () => {
          this.renderer.setStyle(element, 'text-decoration', 'underline');

          if (isLastElement) {
            this.renderer.setStyle(element, 'color', 'red');
          }
        });

        this.renderer.listen(element, 'mouseleave', () => {
          this.renderer.removeStyle(element, 'text-decoration');
        });
      });
    }
  }

  buildBreadcrumb() {
    const previousQueryParams = window.history.state?.previousQueryParams;

    this.breadCrump = [
      { label: this.i18nService.instant('pages.orderDetails.breadcrumb.myAccount'), url: '/my-account' },
      { 
        label: this.i18nService.instant('shared.labels.orders'), 
        url: '/my-account/orders',
        queryParams: previousQueryParams && Object.keys(previousQueryParams).length > 0 
          ? previousQueryParams 
          : undefined
      },
      { label: this.i18nService.instant('pages.orderDetails.breadcrumb.details'), url: '/my-account/orders/details' },
    ];

    return this.breadCrump;
  }

  loadOrderData() {
    this.isLoading = true;
    this.error = null;

    this.orderService
      .getOrderDetailsByUuid(this.orderId)
      .pipe(timeout(10000))
      .subscribe({
        next: (response: OrderDetailsResponse) => {
          if (response.status === 'success' && response.data) {
            this.orderDetails = response.data;
            if (this.isEmpty(this.orderDetails)) {
              this.error = this.i18nService.instant('pages.orderDetails.messages.notFound');
            }
            // purchase
            try {
              const items: Ga4Item[] =
                this.orderDetails.lineItems?.length && this.orderDetails.lineItems.length > 0
                  ? this.orderDetails.lineItems.map((li: CustomerOrderLineItem) => ({
                      item_id: li.productSku || li.productUuid,
                      item_name: li.productName,
                      item_category: undefined,
                      item_variant: li.plan || undefined,
                      price: li.price,
                      quantity: li.quantity,
                    }))
                  : (this.orderDetails.products || []).map((p: OrderProduct) => ({
                      item_id: p.sku || p.uuid,
                      item_name: p.name,
                      item_category: undefined,
                      item_variant: p.plan || undefined,
                      price: p.price,
                      quantity: p.quantity,
                    }));
              this.gtm.purchase(
                this.orderDetails.identifier || this.orderDetails.uuid,
                items,
                this.orderDetails.total || items.reduce((acc, i) => acc + (i.price ?? 0) * (i.quantity ?? 1), 0)
              );
            } catch {
              console.log('Error sending purchase to GA4');
            }
          } else {
            this.orderDetails = {} as OrderData;
          }
          this.isLoading = false;
        },
        error: error => {
          this.error = this.i18nService.instant('pages.orderDetails.messages.loadError');
          this.isLoading = false;
          this.orderDetails = {} as OrderData;
        },
      });
  }

  isEmpty(data: OrderData): boolean {
    if (
      !data.status &&
      !data.total &&
      !data.identifier &&
      (data.products?.length ?? 0) === 0 &&
      (data.lineItems?.length ?? 0) === 0
    )
      return true;
    return false;
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.orderService.clearSelectedOrderId();
    this.orderService.clearOrderDetailsCreditContext();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
